import {Injectable} from '@angular/core';
import {JobResource} from '../resource/job.resource';
import {Job} from '../interfaces/jobs.dto';

// Poll schedule: fast initial polling for the common 2-8s AI response window,
// backing off for long-running jobs (document conversion, large map generation)
// so we don't hammer the server when the user has to wait anyway.
// [intervalMs, minElapsedMs] — use this interval once elapsed >= minElapsedMs.
const POLL_SCHEDULE: Array<{interval: number; untilMs: number}> = [
  {interval: 400, untilMs: 5000},   // 0-5s: poll every 400ms
  {interval: 1000, untilMs: 20000}, // 5-20s: every 1s
  {interval: 2500, untilMs: Infinity} // 20s+: every 2.5s
];

interface PollSession {
  timeout: any;
  cancelled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class JobService {
  // MAP-1317: tracking is keyed by jobId so two concurrent generations (e.g.
  // two smap creations launched back-to-back from the SMAPS menu, or an
  // assistant request alongside a backgrounded smap import) can both reach
  // their completion handler. The previous single-slot design silently
  // orphaned the older tracking on every new trackJob/cleanup call, which is
  // why the green success toast and the lottie animation could hang forever.
  private sessions = new Map<string, PollSession>();

  constructor(private jobResource: JobResource) {
  }

  getJob(jobId: string) {
    return this.jobResource.getJob(jobId);
  }

  /**
   * Poll a job by ID until it reaches a terminal state (COMPLETED / FAILED).
   * Calling trackJob with the same jobId replaces the previous session — used
   * by the assistant when reattaching to an in-flight stream after close/reopen
   * (MAP-1443).
   */
  trackJob(
    jobId: string,
    onUpdate: (job: Job) => void,
  ): void {
    this.cancelSession(jobId);
    const session: PollSession = {timeout: null, cancelled: false};
    this.sessions.set(jobId, session);

    const startedAt = Date.now();

    const nextInterval = (): number => {
      const elapsed = Date.now() - startedAt;
      for (const step of POLL_SCHEDULE) {
        if (elapsed < step.untilMs) return step.interval;
      }
      return POLL_SCHEDULE[POLL_SCHEDULE.length - 1].interval;
    };

    const scheduleNext = () => {
      session.timeout = setTimeout(poll, nextInterval());
    };

    const poll = () => {
      this.jobResource.getJob(jobId).subscribe({
        next: (job) => {
          if (session.cancelled) return;
          onUpdate(job);
          if (session.cancelled) return;
          if (job.status === 'COMPLETED' || job.status === 'FAILED') {
            this.cancelSession(jobId);
          } else {
            scheduleNext();
          }
        },
        error: (err) => {
          if (session.cancelled) return;
          console.warn('Job poll failed, will retry:', err);
          scheduleNext();
        }
      });
    };

    poll();
  }

  /**
   * Cancel polling for a specific job, or for all tracked jobs when no id is
   * passed. Callers should prefer the scoped form so they don't accidentally
   * abort an unrelated generation that's still in flight.
   */
  cleanup(jobId?: string): void {
    if (jobId) {
      this.cancelSession(jobId);
      return;
    }
    for (const id of Array.from(this.sessions.keys())) {
      this.cancelSession(id);
    }
  }

  private cancelSession(jobId: string): void {
    const session = this.sessions.get(jobId);
    if (!session) return;
    session.cancelled = true;
    if (session.timeout) {
      clearTimeout(session.timeout);
      session.timeout = null;
    }
    this.sessions.delete(jobId);
  }
}
